/**
 * A type's page in Pages (`project/:p/page/type/:typeId`): where it sits, its
 * name, its prose (the `type-page:<typeId>` document, registered the first
 * time anyone opens the page, as on the desktop), then the editable table of
 * every item of the type and its subtypes, with a Where column.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { asTeamDocumentId, asTeamProjectId, createNamedPageViewsController, type CollabEditorMountOptions, type NamedPageViewsController } from '@nimbalyst/collab-bundle/editor';
import { loadSetPageType, useCollabDocsUI, useCollabPagesState, useSharedFolders } from '@nimbalyst/collab-bundle/docs-ui';
import { TrackerSurfaceMessage, useTrackerDataSelector } from '@nimbalyst/collab-bundle/trackers-ui';
import { CollabEditorMount } from '../collab/CollabEditorMount';
import { useDocsShell } from '../collab/CollabDocsShell';
import { CollabLoadingPanel } from '../components/Chrome';
import { consoleConfig } from '../config';
import type { OrganizationRouteContext } from '../routes/workspaceContext';
import { orgPageItemPath } from '../routing';
import { useOptionalProjectTrackerSession } from '../trackers/ProjectTrackerSession';
import { usePagesTrackers } from './PagesTrackers';
import { usePageModule } from './pageModule';
import { pagesPlacedViewTypes } from './placedViewTypes';
import { embeddedReadOnlyTrackerEmbeds, isEmbedded } from '../embedded/embedMarker';

export function WebTypePageRoute() {
  const { typeId } = useParams<{ typeId: string }>();
  const { available, resolver } = usePagesTrackers();
  const module = usePageModule();
  if (!available || !module || !resolver || !typeId) {
    return <CollabLoadingPanel title="Opening this type" detail="Connecting to your team's pages." />;
  }
  if (!resolver.typeName(typeId)) {
    return <TrackerSurfaceMessage icon="search_off" message="This type is not in this project." testId="type-page-missing" />;
  }
  return <TypePage typeId={typeId} typeName={resolver.typeName(typeId)!} typeLabel={resolver.typeLabel?.(typeId) ?? typeId} module={module} />;
}

type ProseState = { kind: 'ready' } | { kind: 'pending' } | { kind: 'failed'; message: string };

function TypePage({ typeId, typeName, typeLabel, module }: {
  typeId: string;
  typeName: string;
  typeLabel: string;
  module: NonNullable<ReturnType<typeof usePageModule>>;
}) {
  const { team, projectId, navigate } = useDocsShell();
  const { teamSession, getTeamJwt } = useOutletContext<OrganizationRouteContext>();
  const projectTrackers = useOptionalProjectTrackerSession();
  const { session } = useCollabDocsUI();
  const { documents, typePlacements, itemPlacements } = useCollabPagesState();
  const folders = useSharedFolders();
  const recordsById = useTrackerDataSelector((state) => state.recordsById);
  const editorKey = JSON.stringify([team.orgId, projectId, typeId]);
  type ReadyEditor = Parameters<NonNullable<CollabEditorMountOptions['onLexicalEditor']>>[0];
  const [viewsEditor, setViewsEditor] = useState<{ key: string; editor: ReadyEditor } | null>(null);
  const onViewsEditor = useCallback((editor: ReadyEditor) => setViewsEditor(current => current?.key === editorKey && current.editor === editor ? current : { key: editorKey, editor }), [editorKey]);
  const proseEditor = viewsEditor?.key === editorKey ? viewsEditor.editor : null;
  // Built here, not in trackers-ui, so that bundle entry never loads the editor graph.
  const [viewsController, setViewsController] = useState<NamedPageViewsController | null>(null);
  useEffect(() => {
    if (!proseEditor) { setViewsController(null); return; }
    const next = createNamedPageViewsController(proseEditor, typeId);
    setViewsController(next);
    return () => next.dispose();
  }, [proseEditor, typeId]);
  const proseId = `type-page:${typeId}`;
  const proseExists = documents.some((document) => document.documentId === proseId);
  const placement = typePlacements.find((candidate) => candidate.typeId === typeId);
  const [prose, setProse] = useState<ProseState>({ kind: proseExists ? 'ready' : 'pending' });

  // The first visit registers the prose page under the type's own parent.
  useEffect(() => {
    if (proseExists) {
      setProse({ kind: 'ready' });
      return;
    }
    let live = true;
    void loadSetPageType()
      .then(({ ensureTypePageDocument }) => ensureTypePageDocument(
        { typeId, typeName, parentFolderId: placement?.parentFolderId ?? null },
        {
          existing: (documentId) => session.getDocuments().find((document) => document.documentId === documentId),
          create: async ({ documentId, requestedName, parentFolderId }) => {
            await session.registerDocument({
              documentId,
              title: requestedName,
              documentType: 'markdown',
              parentFolderId,
              ...(parentFolderId && placement?.parentKind ? { parentKind: placement.parentKind } : {}),
              metadata: { metadataVersion: 2, fileExtension: '.md', editorId: 'builtin.lexical' },
            });
            const created = session.getDocuments().find((document) => document.documentId === documentId);
            if (!created) throw new Error('the type page was not registered');
            return created;
          },
          refusal: () => null,
        },
      ))
      .then(() => { if (live) setProse({ kind: 'ready' }); })
      .catch((error: unknown) => {
        if (live) setProse({ kind: 'failed', message: error instanceof Error ? error.message : String(error) });
      });
    return () => { live = false; };
  }, [proseExists, typeId, typeName, placement?.parentFolderId, placement?.parentKind, session]);

  const ancestors = useMemo(
    () => module.trackerPageCrumbFolders(typeId, typePlacements, folders, { itemPlacements, item: module.crumbItemLookup(recordsById) }),
    [module, typeId, typePlacements, folders, itemPlacements, recordsById],
  );
  const source = useMemo(() => ({
    kind: 'team-room' as const,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    room: { orgId: teamSession.orgId, projectId: asTeamProjectId(projectId), documentId: asTeamDocumentId(proseId) },
    auth: { scope: 'team' as const, memberId: teamSession.memberId, getTeamJwt },
  }), [teamSession.orgId, teamSession.memberId, projectId, proseId, getTeamJwt]);
  // The editor's presence label falls back from name to email to "Teammate".
  const selfEmail = projectTrackers?.members.find((member) => member.memberId === teamSession.memberId)?.email;
  const user = useMemo(
    () => ({ memberId: teamSession.memberId, name: projectTrackers?.presenceName, email: selfEmail }),
    [teamSession.memberId, projectTrackers?.presenceName, selfEmail],
  );
  const { TypePageTable } = module;
  const [searchParams, setSearchParams] = useSearchParams();
  const viewQuery = searchParams.get('view');
  const handoff = useMemo(() => {
    if (viewQuery === null) return {};
    try { return { view: module.parsePlacedViewHandoff(viewQuery) }; }
    catch { return { error: 'This view definition is invalid. Return to the source page or choose All.' }; }
  }, [module, viewQuery]);
  const clearView = () => { if (viewQuery === null) return; const next = new URLSearchParams(searchParams); next.delete('view'); setSearchParams(next); };

  return (
    <div className="web-type-page type-page-tab flex min-h-full flex-col" data-testid="web-type-page" {...embeddedReadOnlyTrackerEmbeds()}>
      <div className="type-page-tab-header px-8 pt-6">
        {ancestors.length > 0 ? (
          <div className="type-page-tab-crumb mb-1 truncate text-xs text-nim-faint">{ancestors.join(' / ')}</div>
        ) : null}
        <h1 className="m-0 text-2xl font-semibold text-nim">{typeName}</h1>
      </div>
      <div className="type-page-tab-prose min-h-[120px]">
        {prose.kind === 'ready' ? (
          <CollabEditorMount
            key={editorKey}
            onLexicalEditor={onViewsEditor}
            source={source}
            user={user}
            // Type pages are read-only in the phone app in v1.
            readOnly={isEmbedded() ? true : undefined}
            trackerReferences={projectTrackers?.references ?? null}
            trackerReferenceAppearance="quiet"
            placedViewTypes={pagesPlacedViewTypes}
          />
        ) : prose.kind === 'failed' ? (
          <div className="px-8 py-3 text-xs text-nim-muted" role="alert">This type&apos;s description could not be opened ({prose.message}).</div>
        ) : null}
      </div>
      <div className="type-page-tab-table-wrap px-8 pb-8">
        {handoff.error ? <div role="alert" className="text-sm text-nim-error">{handoff.error} <button type="button" onClick={clearView}>All</button></div> : <TypePageTable
          key={editorKey}
          viewsController={viewsController}
          viewScope={{ orgId: team.orgId, projectId }}
          typeId={typeId}
          temporaryView={handoff.view}
          onClearTemporaryView={clearView}
          typeLabel={typeLabel}
          rootLabel="Team"
          itemPlacements={itemPlacements}
          // The page tree projects every page as a folder row, which is what Where reads.
          pages={folders}
          itemTitle={(itemId) => {
            const record = recordsById.get(itemId);
            return record ? String(record.fields.title ?? '') : null;
          }}
          onOpenItem={(itemId) => navigate(orgPageItemPath(team, projectId, recordsById.get(itemId)?.issueKey ?? itemId))}
        />}
      </div>
    </div>
  );
}
